import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  WorkLogImageGeo,
  WorkLogService,
} from 'src/app/shared/services/work-log.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

interface PendingAttachment {
  file: File;
  preview: string;
  latitude?: number;
  longitude?: number;
  fromCamera: boolean;
}

@Component({
  selector: 'app-work-dialog',
  templateUrl: './work-dialog.component.html',
  styleUrl: './work-dialog.component.css',
})
export class WorkDialogComponent {
  @ViewChild('cameraInput') cameraInput!: ElementRef<HTMLInputElement>;
  @ViewChild('galleryInput') galleryInput!: ElementRef<HTMLInputElement>;

  workLogForm: FormGroup;
  attachments: PendingAttachment[] = [];
  fileError = '';
  serverError = '';
  submitting = false;
  capturingGeo = false;
  projectId: number | null = null;
  projectName = '';

  readonly today = new Date().toISOString().slice(0, 10);

  constructor(
    private workLogService: WorkLogService,
    private toastrMsgService: ToastrMsgService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<WorkDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (data && typeof data === 'object') {
      this.projectId = Number(data.project_id ?? data.projectId ?? data.id) || null;
      this.projectName = data.project_name ?? data.projectName ?? '';
    } else if (data != null) {
      this.projectId = Number(data) || null;
    }

    this.workLogForm = this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      logDate: [this.today, Validators.required],
      hoursWorked: [0, [Validators.min(0), Validators.max(24)]],
      progressPercentage: [0, [Validators.min(0), Validators.max(100)]],
      status: ['in_progress', Validators.required],
      priority: ['medium', Validators.required],
    });
  }

  openGalleryPicker(): void {
    if (this.submitting) return;
    this.galleryInput?.nativeElement?.click();
  }

  async openCameraCapture(): Promise<void> {
    if (this.submitting || this.capturingGeo) return;
    this.fileError = '';
    this.capturingGeo = true;

    try {
      const position = await this.getCurrentPosition();
      if (!position) {
        this.fileError =
          'Location is required for geo-tagged photos. Enable GPS and allow location access.';
        return;
      }

      this.pendingGeo = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      this.cameraInput?.nativeElement?.click();
    } finally {
      this.capturingGeo = false;
    }
  }

  private pendingGeo: { latitude: number; longitude: number } | null = null;

  onGallerySelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    this.fileError = '';
    if (!files?.length) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        this.fileError = 'Only image files (JPG, PNG) are allowed.';
        continue;
      }
      this.addAttachment(file, false);
    }
    target.value = '';
  }

  onCameraSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    this.fileError = '';
    if (!file) {
      this.pendingGeo = null;
      return;
    }
    if (!file.type.startsWith('image/')) {
      this.fileError = 'Only image files are allowed.';
      this.pendingGeo = null;
      target.value = '';
      return;
    }

    this.addAttachment(
      file,
      true,
      this.pendingGeo?.latitude,
      this.pendingGeo?.longitude
    );
    this.pendingGeo = null;
    target.value = '';
  }

  private addAttachment(
    file: File,
    fromCamera: boolean,
    latitude?: number,
    longitude?: number
  ): void {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.attachments.push({
        file,
        preview: String(e.target?.result ?? ''),
        latitude,
        longitude,
        fromCamera,
      });
    };
    reader.readAsDataURL(file);
  }

  removeAttachment(index: number): void {
    if (this.submitting) return;
    this.attachments.splice(index, 1);
  }

  save(): void {
    this.serverError = '';
    this.fileError = '';

    if (this.workLogForm.invalid) {
      this.workLogForm.markAllAsTouched();
      return;
    }

    if (!this.projectId) {
      this.serverError = 'Project context is missing. Please reopen the dialog.';
      return;
    }

    const v = this.workLogForm.value;
    this.submitting = true;

    this.workLogService
      .createWorkLog({
        projectId: this.projectId,
        logDate: v.logDate,
        title: String(v.title || '').trim(),
        description: String(v.description || '').trim(),
        hoursWorked: Number(v.hoursWorked) || 0,
        progressPercentage: Number(v.progressPercentage) || 0,
        status: v.status,
        priority: v.priority,
      })
      .pipe(
        switchMap((res) => {
          const workLogId = res?.data?.id;
          if (!workLogId || this.attachments.length === 0) {
            return of(res);
          }
          const files = this.attachments.map((a) => a.file);
          const imageGeo: WorkLogImageGeo[] = this.attachments.map((a) => ({
            ...(a.latitude != null ? { latitude: a.latitude } : {}),
            ...(a.longitude != null ? { longitude: a.longitude } : {}),
          }));
          return this.workLogService
            .uploadImages(workLogId, files, { imageGeo })
            .pipe(
              catchError((uploadErr) =>
                throwError(() => ({
                  ...uploadErr,
                  workLogCreated: true,
                  workLogId,
                }))
              ),
              switchMap(() => of(res))
            );
        })
      )
      .subscribe({
        next: (res) => {
          this.submitting = false;
          this.toastrMsgService.success('Work log saved successfully');
          this.dialogRef.close({ success: true, data: res?.data });
        },
        error: (err) => {
          this.submitting = false;
          this.serverError = this.extractErrorMessage(err);
        },
      });
  }

  cancel(): void {
    if (this.submitting) return;
    this.dialogRef.close();
  }

  formatCoords(lat?: number, lng?: number): string {
    if (lat == null || lng == null) return '';
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }

  private getCurrentPosition(): Promise<GeolocationPosition | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  }

  private extractErrorMessage(err: any): string {
    if (!err) return 'Something went wrong. Please try again.';
    if (err.workLogCreated) {
      const base = this.extractErrorMessage({
        error: err.error,
        message: err.message,
        statusText: err.statusText,
      });
      return `Work log was saved (#${err.workLogId}) but photos could not be uploaded. ${base}`;
    }
    const body = err.error ?? err;
    return (
      body?.errorMessage ||
      body?.message ||
      body?.error ||
      err?.statusText ||
      err?.message ||
      'Something went wrong. Please try again.'
    );
  }
}
